// src/components/Base/BaseComponent.tsx (简化版)
import React, { Component } from 'react';
import { BaseComponentProps, BaseComponentState } from '../../../types/base';

// 支持泛型
export abstract class BaseComponent<P extends BaseComponentProps = BaseComponentProps, S extends BaseComponentState = BaseComponentState> extends Component<P, S> {

    constructor(props: P) {
        super(props);
        this.state = {
            ...(this.getInitialState() as S)
        };
    }

    protected getInitialState(): Partial<S> {
        return {};
    }

    protected setLoading = (loading: boolean): void => {
        this.setState({ loading } as Pick<S, keyof S>);
    }

    protected setError = (error: string | null): void => {
        this.setState({ error } as Pick<S, keyof S>);
    }

    protected safeSetState = (state: Partial<S>, callback?: () => void): void => {
        try {
            // 将 Partial<S> 断言为 Pick<S, keyof S>，满足 setState 的类型要求
            this.setState(state as Pick<S, keyof S>, callback);
        } catch (error) {
            console.error('设置状态时发生错误:', error);
            this.setError('状态更新失败');
        }
    }

    protected handleAsyncOperation = async (
        operation: () => Promise<any>,
        options: {
            loading?: boolean;
            errorMessage?: string;
            onSuccess?: (result: any) => void;
            onError?: (error: any) => void;
        } = {}
    ): Promise<any> => {
        const {
            loading = true,
            errorMessage = '操作失败',
            onSuccess,
            onError
        } = options;

        try {
            if (loading) this.setLoading(true);
            this.setError(null);

            const result = await operation();

            if (onSuccess) onSuccess(result);
            return result;
        } catch (error) {
            console.error('异步操作错误:', error);
            const message = error instanceof Error ? error.message : errorMessage;
            this.setError(message);

            if (onError) onError(error);
            return null;
        } finally {
            if (loading) this.setLoading(false);
        }
    }

    protected renderLoading = (): React.ReactNode => {
        return <div className="loading">加载中...</div>;
    }

    protected renderError = (): React.ReactNode => {
        const { error } = this.state;
        if (!error) return null;

        return (
            <div className="error-message">
                <p>{error}</p>
                <button onClick={() => this.setError(null)}>重试</button>
            </div>
        );
    }

    abstract renderContent(): React.ReactNode;

    render() {
        const { loading, error } = this.state;
        const { className, style, children } = this.props;

        return (
            <div className={className} style={style}>
                {loading && this.renderLoading()}
                {error && this.renderError()}
                {!loading && !error && this.renderContent()}
                {children}
            </div>
        );
    }
}